import { isAxiosError } from "axios";
import { type ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/icon/Icon";
import { Alert, Button, Card, SegmentedControl, Spinner, Tabs, Tag } from "@/components/ui";
import { useAdminMe, useAdminOverview, useAdminWorkspaces } from "@/lib/api/queries";
import type {
  AdminBucket,
  AdminFeatureUsage,
  AdminFunnelRow,
  AdminHeatCell,
  AdminKpi,
  AdminNamedCount,
  AdminWorkspaceRow,
} from "@/lib/api/types";
import {
  barHeight,
  barWidth,
  count,
  formatHours,
  heatOpacity,
  heatmapGrid,
  HOURS,
  pct,
  shortDate,
  type SortDir,
  type SortKey,
  sortRows,
  WEEKDAYS,
} from "./metrics";

const EMPTY_TEXT = "이 기간에는 만든 주문이 없어요. 기간을 넓혀 볼까요?";

const DAY_OPTIONS = [
  { value: "7", label: "7일" },
  { value: "30", label: "30일" },
  { value: "90", label: "90일" },
];

function EmptyNote({ children = EMPTY_TEXT }: { children?: ReactNode }) {
  return <p className="py-10 text-center wds-body-2 text-label-alternative">{children}</p>;
}

function Section({
  title,
  desc,
  children,
}: {
  title: string;
  desc?: string;
  children: ReactNode;
}) {
  return (
    <Card variant="outline" padding={16}>
      <h2 className="wds-headline-2 text-label-strong">{title}</h2>
      {desc && <p className="mt-0.5 wds-caption-1 text-label-assistive">{desc}</p>}
      <div className="mt-3">{children}</div>
    </Card>
  );
}

type Tile = { label: string; value: string; sub: string };

function TileGrid({ tiles }: { tiles: Tile[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
      {tiles.map((tile) => (
        <Card key={tile.label} variant="outline" padding={16}>
          <p className="wds-caption-1 text-label-alternative">{tile.label}</p>
          <p className="mt-1 wds-title-3 text-label-strong tabular-nums">{tile.value}</p>
          <p className="wds-caption-1 text-label-assistive tabular-nums">{tile.sub}</p>
        </Card>
      ))}
    </div>
  );
}

function KpiGrid({ kpi }: { kpi: AdminKpi }) {
  // 직전 기간 대비 증감은 서버가 주지 않는다 — 없는 숫자를 지어내지 않고 값만 찍는다.
  const tiles: Tile[] = [
    {
      label: "활성 워크스페이스",
      value: count(kpi.active_workspaces),
      sub: `전체의 ${pct(kpi.active_ratio)}`,
    },
    { label: "기간 내 주문", value: count(kpi.orders_total), sub: "만든 주문 링크" },
    { label: "제출", value: count(kpi.submissions_total), sub: "참여자가 낸 주문" },
    { label: "제출 충족률", value: pct(kpi.fill_rate), sub: "초대 인원 대비" },
    { label: "마감률", value: pct(kpi.close_rate), sub: "만든 주문 대비" },
    { label: "자유 입력률", value: pct(kpi.custom_menu_rate), sub: "메뉴판에 없던 주문" },
    { label: "재주문율", value: pct(kpi.reorder_rate), sub: "2번 이상 주문한 곳" },
    {
      label: "신규 워크스페이스",
      value: count(kpi.workspaces_new),
      sub: `전체 ${count(kpi.workspaces_total)}곳`,
    },
  ];
  return <TileGrid tiles={tiles} />;
}

function UserKpiGrid({ kpi }: { kpi: AdminKpi }) {
  const tiles: Tile[] = [
    { label: "누적 사용자", value: count(kpi.users_total), sub: "로그인한 적 있는 사람" },
    { label: "신규 사용자", value: count(kpi.users_new), sub: "이 기간 첫 로그인" },
    { label: "활성 사용자", value: count(kpi.users_active), sub: "이 기간 접속" },
    { label: "재방문율", value: pct(kpi.users_returning_rate), sub: "두 번 이상 로그인" },
  ];
  return <TileGrid tiles={tiles} />;
}

function FeatureKpiGrid({ usage }: { usage: AdminFeatureUsage }) {
  const tiles: Tile[] = [
    {
      label: "건배사 생성",
      value: count(usage.toast_total),
      sub: `${count(usage.toast_users)}명이 사용`,
    },
    { label: "n행시 비중", value: pct(usage.acrostic_ratio), sub: "건배사 시도 대비" },
    // 재시도가 잦으면 프롬프트를 고칠 신호다. 여태 이 숫자를 볼 방법이 없었다.
    { label: "머리글자 재시도율", value: pct(usage.toast_retry_rate), sub: "한 번 더 부른 비율" },
    { label: "건배사 실패율", value: pct(usage.toast_fail_rate), sub: "사용자가 사유를 본 비율" },
    {
      label: "내기 실행",
      value: count(usage.draw_total),
      sub: `${count(usage.draw_users)}명이 사용`,
    },
    { label: "사다리타기 비중", value: pct(usage.ladder_ratio), sub: "내기 대비" },
    {
      label: "대화 주제 생성",
      value: count(usage.icebreaker_total),
      sub: `${count(usage.icebreaker_users)}명이 사용`,
    },
    // 재시도율이 없다 — 대화 주제에는 기계가 검사하는 규칙(건배사의 머리글자)이 없다.
    {
      label: "대화 주제 실패율",
      value: pct(usage.icebreaker_fail_rate),
      sub: "사용자가 사유를 본 비율",
    },
  ];
  return <TileGrid tiles={tiles} />;
}

function TrendBars({
  points,
  label,
}: { points: Array<{ date: string; value: number }>; label: string }) {
  const total = points.reduce((sum, p) => sum + p.value, 0);
  if (points.length === 0 || total === 0) return <EmptyNote />;
  const max = Math.max(...points.map((p) => p.value), 1);
  const marks = [points[0], points[Math.floor((points.length - 1) / 2)], points[points.length - 1]];
  return (
    <>
      <div
        role="img"
        aria-label={`일별 ${label} 막대 그래프. 합계 ${count(total)}건, 하루 최고 ${count(max)}건.`}
      >
        {/* 90일을 폰에서 보면 막대 사이 간격(2px×89)이 폭의 절반을 먹어 막대가
            머리카락이 된다. 칸이 많아지면 간격을 버리고 폭을 막대에 준다. */}
        <ol
          className={`flex h-36 items-end sm:h-48 ${points.length > 45 ? "gap-0" : "gap-0.5"}`}
          aria-hidden="true"
        >
          {points.map((p) => (
            <li
              key={p.date}
              className="flex h-full flex-1 items-end"
              title={`${p.date} ${count(p.value)}건`}
            >
              <div
                className="w-full rounded-xs bg-primary"
                style={{ height: barHeight(p.value, max) }}
              />
            </li>
          ))}
        </ol>
      </div>
      <div className="mt-2 flex justify-between wds-caption-2 text-label-assistive tabular-nums">
        {marks.map((mark, i) => (
          <span key={`${mark.date}-${i}`}>{shortDate(mark.date)}</span>
        ))}
      </div>
      <ul className="sr-only">
        {points.map((p) => (
          <li key={p.date}>{`${p.date} ${label} ${p.value}건`}</li>
        ))}
      </ul>
    </>
  );
}

/** 탭으로 계열을 골라 같은 막대 차트를 그린다. 주문/제출과 사용자 증가가 같은 모양이라
 *  한 컴포넌트로 쓴다 — `pick` 만 계열별로 다르다. */
function SeriesBars<T extends { date: string }>({
  options,
  rows,
  pick,
}: {
  options: Array<{ value: string; label: string }>;
  rows: T[];
  pick: (row: T, series: string) => number;
}) {
  const [series, setSeries] = useState(options[0]?.value ?? "");
  const label = options.find((option) => option.value === series)?.label ?? "";
  const points = rows.map((row) => ({ date: row.date, value: pick(row, series) }));
  return (
    <>
      <Tabs items={options} value={series} onChange={setSeries} />
      <div className="mt-4">
        <TrendBars points={points} label={label} />
      </div>
    </>
  );
}

function Funnel({ rows }: { rows: AdminFunnelRow[] }) {
  const base = rows[0]?.count ?? 0;
  if (rows.length === 0 || base === 0) {
    return <EmptyNote>이 기간에 새로 생긴 워크스페이스가 없어요.</EmptyNote>;
  }
  return (
    <ol className="flex flex-col gap-3">
      {rows.map((row) => {
        const dropPct = row.conversion_pct === null ? null : 100 - row.conversion_pct;
        return (
          <li key={row.stage}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="wds-label-1 text-label-normal">{row.label}</span>
              <span className="wds-label-2 text-label-alternative tabular-nums">
                {count(row.count)}곳
                {row.conversion_pct === null ? "" : ` · ${pct(row.conversion_pct)}`}
              </span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-fill-alternative">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${barWidth(row.count, base)}%` }}
              />
            </div>
            {dropPct !== null && dropPct > 40 && row.dropped !== null && (
              <p className="mt-1.5">
                <Tag color="cautionary" size="small">
                  이탈 {count(row.dropped)}곳 · {pct(dropPct)}
                </Tag>
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Heatmap({ cells }: { cells: AdminHeatCell[] }) {
  const grid = heatmapGrid(cells);
  const values = grid.flat();
  const max = Math.max(...values, 0);
  if (max === 0) return <EmptyNote />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[56rem] border-collapse">
        <caption className="sr-only">요일과 시각(KST)별로 만든 주문 수</caption>
        <thead>
          <tr>
            <th scope="col" className="w-8 text-left">
              <span className="sr-only">요일</span>
            </th>
            {HOURS.map((hour) => (
              <th
                key={hour}
                scope="col"
                className="p-0.5 wds-caption-2 text-label-assistive tabular-nums"
              >
                {hour}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, weekday) => (
            <tr key={WEEKDAYS[weekday]}>
              <th scope="row" className="pr-2 text-left wds-caption-1 text-label-neutral">
                {WEEKDAYS[weekday]}
              </th>
              {row.map((value, hour) => (
                <td key={`${WEEKDAYS[weekday]}-${hour}`} className="p-0.5">
                  {/* 색은 보조 채널 — 불투명도 상한 0.55 를 넘기면 위에 찍는 숫자가 대비 미달이다. */}
                  <div className="relative flex h-9 items-center justify-center rounded-xs bg-fill-alternative">
                    <div
                      className="absolute inset-0 rounded-xs bg-primary"
                      style={{ opacity: heatOpacity(value, max) }}
                      aria-hidden="true"
                    />
                    <span className="relative wds-caption-1 text-label-normal tabular-nums">
                      {value}
                    </span>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 버킷 분포와 Top N 목록이 같은 모양이라 한 컴포넌트로 쓴다. `ranked` 면 순위를 붙인다. */
function BarList({
  items,
  unit,
  ranked = false,
  empty,
}: {
  items: AdminBucket[];
  unit: string;
  ranked?: boolean;
  empty?: string;
}) {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  if (items.length === 0 || total === 0) return <EmptyNote>{empty}</EmptyNote>;
  const max = Math.max(...items.map((item) => item.count), 1);
  return (
    <ol className="flex flex-col gap-2">
      {items.map((item, i) => (
        <li key={item.label}>
          <div className="flex items-baseline justify-between gap-2">
            {/* 라벨은 참여자가 직접 친 문자열일 수 있다 — 안 끊기는 긴 한 덩어리가
                그리드 트랙을 밀지 않게 min-w-0 + break-all 로 가둔다. */}
            <span className="min-w-0 break-all wds-label-2 text-label-normal">
              {ranked ? `${i + 1}. ` : ""}
              {item.label}
            </span>
            <span className="shrink-0 wds-label-2 text-label-alternative tabular-nums">
              {count(item.count)}
              {unit}
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-fill-alternative">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${barWidth(item.count, max)}%` }}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}

function named(items: AdminNamedCount[]): AdminBucket[] {
  return items.map((item) => ({ label: item.name, count: item.count }));
}

type Column = {
  key: SortKey;
  label: string;
  numeric: boolean;
  render: (row: AdminWorkspaceRow) => ReactNode;
};

const COLUMNS: Column[] = [
  { key: "name", label: "이름", numeric: false, render: (r) => r.name },
  {
    key: "owner_name",
    label: "주인",
    numeric: false,
    // 로그인 기록이 없으면 uid 만 있는데 그건 난수라 보여줄 값이 없다.
    // 이름이 빈 계정은 이메일로 대신 부른다.
    render: (r) => r.owner_name || r.owner_email || "-",
  },
  { key: "member_count", label: "멤버", numeric: true, render: (r) => count(r.member_count) },
  {
    key: "people_active",
    label: "활성 명단",
    numeric: true,
    render: (r) => count(r.people_active),
  },
  { key: "orders_total", label: "총 주문", numeric: true, render: (r) => count(r.orders_total) },
  {
    key: "orders_recent",
    label: "최근 주문",
    numeric: true,
    render: (r) => count(r.orders_recent),
  },
  {
    key: "dormant_days",
    label: "휴면일",
    numeric: true,
    render: (r) =>
      r.ever_ordered ? (
        `${count(r.dormant_days)}일`
      ) : (
        <Tag color="cautionary" size="small">
          주문 없음
        </Tag>
      ),
  },
  { key: "fill_rate", label: "충족률", numeric: true, render: (r) => pct(r.fill_rate) },
  { key: "close_rate", label: "마감률", numeric: true, render: (r) => pct(r.close_rate) },
  {
    key: "custom_menu_rate",
    label: "자유입력률",
    numeric: true,
    render: (r) => pct(r.custom_menu_rate),
  },
  { key: "seed_ratio", label: "시드 비중", numeric: true, render: (r) => pct(r.seed_ratio) },
  {
    key: "first_order_hours",
    label: "첫 주문 소요",
    numeric: true,
    render: (r) => formatHours(r.first_order_hours),
  },
];

function WorkspaceTable({ rows }: { rows: AdminWorkspaceRow[] }) {
  const [key, setKey] = useState<SortKey>("orders_recent");
  const [dir, setDir] = useState<SortDir>("desc");
  const sorted = useMemo(() => sortRows(rows, key, dir), [rows, key, dir]);

  function toggle(next: SortKey) {
    if (next === key) {
      setDir(dir === "asc" ? "desc" : "asc");
      return;
    }
    setKey(next);
    setDir(next === "name" ? "asc" : "desc");
  }

  if (rows.length === 0) return <EmptyNote>아직 워크스페이스가 없어요.</EmptyNote>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[64rem] wds-body-2">
        <caption className="sr-only">워크스페이스별 사용 지표. 열 제목을 눌러 정렬해요.</caption>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                scope="col"
                aria-sort={
                  key === column.key ? (dir === "asc" ? "ascending" : "descending") : "none"
                }
                className={`border-line-neutral border-b p-2 wds-label-2 text-label-alternative ${
                  column.numeric ? "text-right" : "text-left"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggle(column.key)}
                  className="inline-flex items-center gap-0.5"
                >
                  {column.label}
                  {key === column.key && (
                    <Icon
                      name={dir === "asc" ? "caretUp" : "caretDown"}
                      size={12}
                      aria-hidden="true"
                    />
                  )}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.id} className="border-line-alternative border-b last:border-0">
              {COLUMNS.map((column) => (
                <td
                  key={column.key}
                  className={`p-2 text-label-normal ${
                    column.numeric ? "text-right tabular-nums" : "text-left"
                  }`}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState("30");
  const [staleDismissed, setStaleDismissed] = useState(false);
  const me = useAdminMe();
  const allowed = me.isSuccess;
  const overview = useAdminOverview(Number(days), allowed);
  const workspaces = useAdminWorkspaces(Number(days), allowed);

  if (me.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (me.isError) {
    // 403 은 로그인 문제가 아니라 역할이 없는 것이라 /login 으로 튕기면 루프가 된다.
    const forbidden = isAxiosError(me.error) && me.error.response?.status === 403;
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="wds-title-3 text-label-strong">
          {forbidden ? "권한이 없어요" : "지표를 불러오지 못했어요"}
        </h1>
        <p className="wds-body-2 text-label-alternative">
          {forbidden ? "이 화면은 운영자만 볼 수 있어요." : "잠시 뒤에 다시 시도해 주세요."}
        </p>
        {forbidden ? (
          <Button variant="outlined" onClick={() => navigate("/")}>
            주문으로 가기
          </Button>
        ) : (
          <Button variant="outlined" onClick={() => me.refetch()}>
            다시 시도
          </Button>
        )}
      </main>
    );
  }

  const data = overview.data;
  const rows = workspaces.data?.rows ?? [];
  const isFetching = overview.isFetching || workspaces.isFetching;
  const failed = overview.isError || workspaces.isError;

  function retry() {
    setStaleDismissed(false);
    overview.refetch();
    workspaces.refetch();
  }

  return (
    <main
      aria-busy={isFetching}
      className="mx-auto flex max-w-layout flex-col gap-5 px-4 py-6 sm:gap-6 sm:px-6 sm:py-10"
    >
      <header className="sticky top-0 z-10 -mx-4 flex flex-col gap-2 bg-background-normal px-4 py-3 sm:-mx-6 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-1 self-start wds-label-2 text-label-alternative"
          >
            <Icon name="arrowLeft" size={16} aria-hidden="true" />
            주문으로
          </button>
          <h1 className="wds-title-1 text-label-strong">운영 지표</h1>
          {data && (
            <p className="wds-caption-1 text-label-assistive">
              최근 {data.days}일 · {new Date(data.generated_at).toLocaleString("ko-KR")} 기준
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {isFetching && <Spinner size={16} />}
          <SegmentedControl items={DAY_OPTIONS} value={days} onChange={setDays} size="small" />
        </div>
      </header>

      {failed && !data && (
        <Alert
          tone="negative"
          title="지표를 불러오지 못했어요"
          action={
            <Button variant="outlined" size="small" onClick={retry}>
              다시 시도
            </Button>
          }
        >
          잠시 뒤에 다시 시도해 주세요.
        </Alert>
      )}
      {failed && data && !staleDismissed && (
        <Alert
          tone="cautionary"
          title="옛 수치를 보고 있어요"
          onClose={() => setStaleDismissed(true)}
        >
          새로 받아오지 못해서 마지막으로 받은 값을 그대로 보여주고 있어요.
        </Alert>
      )}

      {data ? (
        <>
          <KpiGrid kpi={data.kpi} />

          <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <Section title="추세" desc="KST 날짜 기준">
              <SeriesBars
                options={[
                  { value: "orders", label: "주문 수" },
                  { value: "submissions", label: "제출 수" },
                ]}
                rows={data.trend}
                pick={(row, series) => (series === "orders" ? row.orders : row.submissions)}
              />
            </Section>
            <Section title="활성화 퍼널" desc={`최근 ${data.days}일에 생긴 워크스페이스 기준`}>
              <Funnel rows={data.funnel} />
            </Section>
          </div>

          <UserKpiGrid kpi={data.kpi} />

          <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <Section
              title="사용자 증가 추이"
              desc="누적선은 이 기간 이전 가입자까지 더한 값에서 시작해요"
            >
              <SeriesBars
                options={[
                  { value: "total", label: "누적" },
                  { value: "new", label: "신규" },
                ]}
                rows={data.user_trend}
                pick={(row, series) => (series === "total" ? row.total_users : row.new_users)}
              />
            </Section>
            <Section title="마지막 접속 분포" desc="기간 필터와 무관하게 전체 사용자 기준이에요">
              <BarList
                items={data.last_seen_distribution}
                unit="명"
                empty="아직 기록된 사용자가 없어요."
              />
            </Section>
          </div>

          <FeatureKpiGrid usage={data.feature_usage} />

          <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <Section
              title="기능 사용 추이"
              desc="기록을 배포한 뒤부터 쌓여요. 그 전 사용량은 남아 있지 않아요"
            >
              <SeriesBars
                options={[
                  { value: "toast", label: "건배사" },
                  { value: "icebreaker", label: "대화 주제" },
                  { value: "draw", label: "내기" },
                ]}
                rows={data.feature_usage.trend}
                pick={(row, series) => {
                  if (series === "toast") return row.toast;
                  if (series === "icebreaker") return row.icebreaker;
                  return row.draw;
                }}
              />
            </Section>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <Section title="건배사 자리 분포" desc="사용자가 고른 '무슨 자리'예요">
                <BarList
                  items={data.feature_usage.occasions}
                  unit="건"
                  empty="아직 건배사를 만든 기록이 없어요."
                />
              </Section>
              <Section
                title="내기 참여 인원"
                desc="뽑기가 서버를 타지 않아 클라이언트가 보고한 값이에요"
              >
                <BarList
                  items={data.feature_usage.draw_size_distribution}
                  unit="건"
                  empty="아직 내기 기록이 없어요."
                />
              </Section>
            </div>
          </div>

          <Section title="요일 × 시각 주문 생성" desc="KST 기준, 숫자는 만든 주문 수예요">
            <Heatmap cells={data.heatmap} />
          </Section>

          {/* grid-cols-N 은 Tailwind 가 repeat(N, minmax(0,1fr)) 로 펴 준다 —
              트랙이 안쪽 내용에 밀리지 않으므로 minmax 를 손으로 쓸 필요가 없다. */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Section title="제출 충족률 분포" desc="주문 하나가 초대 인원 중 몇 %를 채웠는지">
              <BarList items={data.fill_distribution} unit="건" />
            </Section>
            <Section title="마감 리드타임 분포" desc="주문을 만들고 마감할 때까지">
              <BarList
                items={data.lead_time_distribution}
                unit="건"
                empty="이 기간에 마감한 주문이 없어요."
              />
            </Section>
            <Section title="주문당 초대 인원 분포" desc="한 링크에 넣은 사람 수">
              <BarList items={data.invite_size_distribution} unit="건" />
            </Section>
            <Section title="메뉴판 출처" desc="주문이 쓴 메뉴판을 어디서 가져왔는지">
              <BarList items={data.menuboard_source} unit="건" />
            </Section>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Section title="메뉴 선택 Top 20" desc="메뉴판에 있는 메뉴 기준">
              <BarList items={named(data.top_menus)} unit="번" ranked />
            </Section>
            <Section
              title="자유 입력 Top 20"
              desc="메뉴판에 없어 직접 적은 주문. 메뉴 커버리지 결손이에요"
            >
              <BarList
                items={named(data.top_custom_menus)}
                unit="번"
                ranked
                empty="직접 적은 주문이 없었어요."
              />
            </Section>
          </div>

          <Section title="워크스페이스" desc="열 제목을 눌러 정렬할 수 있어요">
            <WorkspaceTable rows={rows} />
          </Section>
        </>
      ) : failed ? null : (
        <div className="flex min-h-[24rem] items-center justify-center">
          <Spinner size={28} />
        </div>
      )}
    </main>
  );
}
